import { useCallback, useEffect, useState } from 'react';
import butterflyApi from '../ButterflyApi';

const PAYMENT_STATUS_LABELS = {
  READY: 'READY',
  APPROVED: 'APPROVED',
  FAILED: 'FAILED',
  CANCELED: 'CANCELED',
}

const PAYMENT_STATUS_BADGE_CLASS = {
  READY: 'bg-amber-50 text-amber-700',
  APPROVED: 'bg-green-50 text-green-700',
  FAILED: 'bg-red-50 text-red-700',
  CANCELED: 'bg-gray-100 text-gray-600',
}

const PRODUCTION_STATUS_BADGE_CLASS = {
  REQUESTED: 'bg-amber-50 text-amber-700',
  ACCEPTED: 'bg-blue-50 text-blue-700',
  IN_PROGRESS: 'bg-indigo-50 text-indigo-700',
  COMPLETED: 'bg-green-50 text-green-700',
  CANCELED: 'bg-gray-100 text-gray-600',
}

const normalizeOrders = (response) => {
  if (!response) return []
  if (Array.isArray(response)) return response
  return response.items || []
}

const formatAmount = (amount) => {
  if (amount === undefined || amount === null) return '-'
  return `${new Intl.NumberFormat('ko-KR').format(amount)} KRW`
}

const formatDate = (datetime) => {
  if (!datetime) return '-'
  return datetime.replace('T', ' ')
}

const getErrorMessage = (error, fallbackMessage) => {
  return (error && error.response && error.response.data && error.response.data.message) || (error && error.message) || fallbackMessage
}

const StatusBadge = ({ value, classMap }) => {
  if (!value) return <span className='text-gray-400'>-</span>

  return <span className={`inline-flex rounded-full px-2 py-1 text-xs font-bold ${classMap[value] || 'bg-gray-100 text-gray-600'}`}>
    {PAYMENT_STATUS_LABELS[value] || value}
  </span>
}

const AppPaymentOrdersManage = () => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setErrorMessage('')

    const response = await butterflyApi.getManageAppPaymentOrders(error => {
      setErrorMessage(getErrorMessage(error, 'Failed to load app payment orders.'))
    })

    setOrders(normalizeOrders(response))
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return <div className='px-4 py-8'>
    <div className='mb-4'>
      <a href='/manage' className='text-blue-600 underline'>Manage</a>
    </div>

    <div className='flex flex-wrap items-center justify-between gap-3'>
      <div>
        <h1 className='text-2xl font-bold'>App Payment Orders</h1>
        <div className='mt-1 text-sm text-gray-500'>All app payment orders, newest first from the API.</div>
      </div>
      <button
        type='button'
        className='rounded bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:bg-gray-300'
        onClick={load}
        disabled={loading}
      >
        Refresh
      </button>
    </div>

    {loading && <div className='mt-6 text-sm text-gray-500'>Loading...</div>}
    {errorMessage && <div className='mt-6 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600'>{errorMessage}</div>}

    <div className='mt-6 overflow-x-auto'>
      <table className='min-w-full border-collapse text-left text-sm'>
        <thead>
          <tr className='border-b bg-gray-50 text-gray-600'>
            <th className='whitespace-nowrap px-3 py-3'>paymentNo</th>
            <th className='whitespace-nowrap px-3 py-3'>orderId</th>
            <th className='whitespace-nowrap px-3 py-3'>amount</th>
            <th className='whitespace-nowrap px-3 py-3'>paymentStatus</th>
            <th className='whitespace-nowrap px-3 py-3'>supporter</th>
            <th className='whitespace-nowrap px-3 py-3'>hero</th>
            <th className='whitespace-nowrap px-3 py-3'>app</th>
            <th className='whitespace-nowrap px-3 py-3'>production</th>
            <th className='whitespace-nowrap px-3 py-3'>registerDatetime</th>
            <th className='whitespace-nowrap px-3 py-3'>approvedDatetime</th>
            <th className='whitespace-nowrap px-3 py-3'>canceledDatetime</th>
            <th className='whitespace-nowrap px-3 py-3'>updateDatetime</th>
          </tr>
        </thead>
        <tbody>
          {orders.map(order => (
            <tr key={order.paymentNo || order.orderId} className='border-b hover:bg-gray-50'>
              <td className='whitespace-nowrap px-3 py-3 text-gray-500'>{order.paymentNo}</td>
              <td className='whitespace-nowrap px-3 py-3 font-mono text-xs'>{order.orderId}</td>
              <td className='whitespace-nowrap px-3 py-3 font-bold'>{formatAmount(order.amount)}</td>
              <td className='whitespace-nowrap px-3 py-3'>
                <StatusBadge value={order.paymentStatus} classMap={PAYMENT_STATUS_BADGE_CLASS} />
              </td>
              <td className='whitespace-nowrap px-3 py-3'>
                <div className='font-bold'>{order.supporterNo ?? '-'}</div>
                <div className='text-xs text-gray-500'>{order.supporterName || '-'}</div>
              </td>
              <td className='whitespace-nowrap px-3 py-3'>
                {
                  order.heroNo
                    ? <a href={`/manage/hero?heroNo=${order.heroNo}`} className='font-bold text-blue-600 underline'>{order.heroNo}</a>
                    : <span className='font-bold'>-</span>
                }
                <div className='text-xs text-gray-500'>{order.heroName || '-'}</div>
              </td>
              <td className='whitespace-nowrap px-3 py-3'>
                <div className='font-bold'>{order.appNo ?? '-'}</div>
                <div className='text-xs text-gray-500'>{order.appName || '-'}</div>
              </td>
              <td className='whitespace-nowrap px-3 py-3'>
                <div className='font-bold'>{order.productionNo ?? '-'}</div>
                <div className='mt-1'>
                  <StatusBadge value={order.productionStatus} classMap={PRODUCTION_STATUS_BADGE_CLASS} />
                </div>
              </td>
              <td className='whitespace-nowrap px-3 py-3 text-gray-500'>{formatDate(order.registerDatetime)}</td>
              <td className='whitespace-nowrap px-3 py-3 text-gray-500'>{formatDate(order.approvedDatetime)}</td>
              <td className='whitespace-nowrap px-3 py-3 text-gray-500'>{formatDate(order.canceledDatetime)}</td>
              <td className='whitespace-nowrap px-3 py-3 text-gray-500'>{formatDate(order.updateDatetime)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {!loading && orders.length === 0 && <div className='border-b px-3 py-8 text-center text-sm text-gray-500'>No orders found.</div>}
    </div>
  </div>
}

export default AppPaymentOrdersManage
